import {
    IFormConditionAction,
    IFormEdit,
    IFormQuestionBase,
    IFormQuestionPresetOption,
    IFormQuestionsTranslationsGroup,
} from './fi-form-shared.d';
import { FormQuestionType } from '@fi-shared/fi-form-shared/fi-form-enums';
import { Injectable } from '@angular/core';
import { AppTextService } from '../app-text/index';
import { IFormQuestion } from '@fi-shared/fi-form-shared/fi-form-shared';
import { AccessRightType, ApplicationSettingsType, FileType } from '@fi-shared/types';
import { fiHasAccessToApplicationType, isDefined } from '@fi-shared/utils';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { groupBy } from '@fi-shared/utils/group-by';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiPrintDownloadComponent } from '@fi-shared/fi-print-download/fi-print-download.component';

let uid = 10000;

@Injectable()
export class FiFormHelperService {
    // we should recalculate conditions on deleting questions or preset options
    private _deleteQuestionEvent$: Subject<void> = new Subject<void>();
    get deleteQuestionEvent$() {
        return this._deleteQuestionEvent$.asObservable();
    }
    triggerDeleteQuestionEvent() {
        this._deleteQuestionEvent$.next();
    }

    private _deleteQuestionPresetOptionEvent$: Subject<void> = new Subject<void>();
    get deleteQuestionPresetOptionEvent$() {
        return this._deleteQuestionPresetOptionEvent$.asObservable();
    }
    triggerDeleteQuestionPresetOptionEvent() {
        this._deleteQuestionPresetOptionEvent$.next();
    }

    private questionsTranslationsGroup: IFormQuestionsTranslationsGroup;

    private _isPrintButtonShown$ = new BehaviorSubject<boolean>(false);
    get isPrintButtonShown$() {
        return this._isPrintButtonShown$.asObservable();
    }
    triggerIsPrintButtonShown(isPrintButtonShown: boolean) {
        this._isPrintButtonShown$.next(isPrintButtonShown);
    }

    private _plainViewMode$ = new BehaviorSubject<boolean>(false);
    get plainViewMode$() {
        return this._plainViewMode$.asObservable();
    }

    private _isCompleteReport$ = new BehaviorSubject<boolean>(false);
    get isCompleteReport$() {
        return this._isCompleteReport$.asObservable();
    }

    constructor(
        private appTextService: AppTextService,
        private fiUserDataService: FiUserDataService,
    ) {}

    downloadPdf(fiPrintDownloadComponent: FiPrintDownloadComponent, args?: { isCompleteReport?: boolean }) {
        if (!args) {
            args = {};
        }
        const isCompleteReport = args.isCompleteReport ?? false;

        return new Promise<void>((resolve, reject) => {
            this._isCompleteReport$.next(args.isCompleteReport ?? false);
            // https://chainformation.atlassian.net/browse/FI-6216
            // we should wait a couple of seconds while all images will be loaded in case of completed report
            fiPrintDownloadComponent.Download({ downloadDelayMs: isCompleteReport ? 2000 : 0 }).then(resolve, reject);
        }).finally(() => {
            this._isCompleteReport$.next(false);
        });
    }

    onGeneratingPdfHtml(isGeneratingPdfHtml: boolean) {
        this._plainViewMode$.next(isGeneratingPdfHtml);
    }

    getQuestionHeaderNameByType(question: IFormQuestion) {
        let headerName: string;
        switch (question.Type) {
            case FormQuestionType.Text:
                return this.appTextService.get('form.answer_text');
            case FormQuestionType.Numeric:
                return this.appTextService.get('form.answer_numeric');
            case FormQuestionType.SingleChoice:
                return this.appTextService.get('forms.answer_single_choice');
            case FormQuestionType.MultipleChoice:
                return this.appTextService.get('forms.answer_multiple_choice');
        }
    }

    getNewQuestion(type: FormQuestionType): IFormQuestion {
        switch (type) {
            case FormQuestionType.Text:
                return this.getNewTextQuestionInstance();
            case FormQuestionType.Numeric:
                return this.getNewNumericQuestionInstance();
            case FormQuestionType.SingleChoice:
                return this.getNewSingleChoiceQuestionInstance();
            case FormQuestionType.MultipleChoice:
                return this.getNewMultipleChoiceQuestionInstance();
            case FormQuestionType.Heading:
                return this.getNewHeadingInstance();
            case FormQuestionType.TextMessage:
                return this.getNewTextMessageInstance();
            case FormQuestionType.MediaBank:
                return this.getNewMediaBankInstance();
            case FormQuestionType.Attachment:
                return this.getNewFileAttachmentInstance();
        }
    }

    getUniqueId(): number {
        return ++uid;
    }

    getIsConditionLabel(selectedSourceQuestion: IFormQuestion): string {
        return selectedSourceQuestion && selectedSourceQuestion.Type === FormQuestionType.MultipleChoice
            ? this.appTextService.get('form.condition_is_any_of')
            : this.appTextService.get('form.condition_is');
    }

    getQuestionName(question: IFormQuestion, selectedLanguageId: number, formQuestions: IFormQuestion[]): string {
        const questionIndex = formQuestions.findIndex((formQuestion) => formQuestion === question);

        if (this.isMediaBankElement(question)) {
            if (questionIndex !== -1) {
                return question.MediaBankFileReferences.length
                    ? `${this.getItemIndexDisplayedNumber(questionIndex)} - ${question.MediaBankFileReferences[0].Name}`
                    : `${this.getItemIndexDisplayedNumber(questionIndex)} - ${this.appTextService.get('common.missing_value')}`;
            }
        } else {
            if (selectedLanguageId) {
                let name = question.Names.find((name) => name.ContentLanguageId === selectedLanguageId);
                return name.Value.trim()
                    ? `${this.getItemIndexDisplayedNumber(questionIndex)} - ${name.Value}`
                    : `${this.getItemIndexDisplayedNumber(questionIndex)} - ${this.appTextService.get('common.missing_value')}`;
            }
        }
    }

    getPresetOptionName(
        option: IFormQuestionPresetOption,
        selectedLanguageId: number,
        selectedSourceQuestion: IFormQuestion,
    ) {
        let optionIndex = selectedSourceQuestion.PresetOptions.findIndex((presetOption) => presetOption === option);

        if (selectedLanguageId) {
            let name = option.Names.find((name) => name.ContentLanguageId === selectedLanguageId);
            return name.Value.trim()
                ? `${this.appTextService.get('forms.option')} ${this.getItemIndexDisplayedNumber(optionIndex)} - ${name.Value}`
                : `${this.appTextService.get('forms.option')} ${this.getItemIndexDisplayedNumber(optionIndex)} - ${this.appTextService.get('common.missing_value')}`;
        }
    }

    /*getAllChildConditions(condition: IFormCondition, result: IFormCondition[] = []): IFormCondition[] {
        if (!condition.ChildItems.length)
            result.push(condition)

        condition.ChildItems.forEach(childItem => {
            if (childItem.ChildItems.length) {
                return this.getAllChildConditions(childItem, result);
            }
            result.push(childItem);
        });
        return result;
    }*/

    isTextOrNumericQuestion(question: IFormQuestionBase): boolean {
        return this.isTextQuestion(question) || this.isNumericQuestion(question);
    }

    isSimpleCondition(question: IFormQuestion): boolean {
        return this.isTextOrNumericQuestion(question) || this.isFileAttachmentElement(question);
    }

    isChoiceQuestion(question: IFormQuestionBase): boolean {
        return this.isMultipleChoiceQuestion(question) || this.isSingleChoiceQuestion(question);
    }

    isTextQuestion(question: IFormQuestionBase): boolean {
        return question.Type === FormQuestionType.Text;
    }

    isNumericQuestion(question: IFormQuestionBase): boolean {
        return question.Type === FormQuestionType.Numeric;
    }

    isMultipleChoiceQuestion(question: IFormQuestionBase): boolean {
        return question.Type === FormQuestionType.MultipleChoice;
    }

    isSingleChoiceQuestion(question: IFormQuestionBase): boolean {
        return question.Type === FormQuestionType.SingleChoice;
    }

    isHeadingElement(question: IFormQuestion): boolean {
        return question.Type === FormQuestionType.Heading;
    }

    isMediaBankElement(question: IFormQuestion): boolean {
        return question.Type === FormQuestionType.MediaBank;
    }

    isFileAttachmentElement(question: IFormQuestionBase): boolean {
        return question.Type === FormQuestionType.Attachment;
    }

    isTextMessageElement(question: IFormQuestion): boolean {
        return question.Type === FormQuestionType.TextMessage;
    }

    isMediaBankImage(question: IFormQuestion): boolean {
        return (
            question.MediaBankFileReferences.length && question.MediaBankFileReferences[0].FileType === FileType.Image
        );
    }

    isMediaBankVideo(question: IFormQuestion): boolean {
        return (
            question.MediaBankFileReferences.length && question.MediaBankFileReferences[0].FileType === FileType.Movie
        );
    }

    isAdditionalTextElement(question: IFormQuestion) {
        return this.isTextMessageElement(question) || this.isHeadingElement(question);
    }

    isEditAllowedByModuleRights() {
        return (
            this.fiUserDataService.userData &&
            fiHasAccessToApplicationType(
                ApplicationSettingsType.FormLocal,
                AccessRightType.Share,
                this.fiUserDataService.getUserData().ModuleAccessRights,
            )
        );
    }

    hasAccessToForms() {
        return (
            this.fiUserDataService.userData &&
            fiHasAccessToApplicationType(
                ApplicationSettingsType.FormLocal,
                AccessRightType.Read,
                this.fiUserDataService.getUserData().ModuleAccessRights,
            )
        );
    }

    hasAccessToFormReports() {
        return (
            this.fiUserDataService.userData &&
            fiHasAccessToApplicationType(
                ApplicationSettingsType.FormReports,
                AccessRightType.Read,
                this.fiUserDataService.getUserData().ModuleAccessRights,
            )
        );
    }

    isNumericOrTextElement(question: IFormQuestion): boolean {
        return this.isTextQuestion(question) || this.isNumericQuestion(question);
    }

    isQuestionMissingValue(question: IFormQuestion, selectedLanguageId: number): boolean {
        if (selectedLanguageId && question.Type !== FormQuestionType.MediaBank) {
            let name = question.Names.find((name) => name.ContentLanguageId === selectedLanguageId);
            return !name.Value.trim() || !name.Value.replace(/\s/g, '');
        }
    }

    private getItemIndexDisplayedNumber(index: number) {
        return index + 1 > 9 ? index + 1 : `0${index + 1}`;
    }

    setQuestionTranslationsGroup(form: { Questions: IFormQuestion[] }) {
        const questionTranslations = [];
        form.Questions.forEach((question) => {
            question.Names.forEach((name) => {
                questionTranslations.push({
                    Value: name.Value,
                    ContentLanguageId: name.ContentLanguageId,
                });
            });
        });
        this.questionsTranslationsGroup = groupBy(questionTranslations, 'ContentLanguageId');
    }

    isAnyQuestionTranslated(contentLangId: number) {
        // todo use questionsTranslationsGroup not very good approach, it can lead to problems if/when
        // we will have more than 1 opened form, try to find out other solution
        if (this.questionsTranslationsGroup && this.questionsTranslationsGroup[contentLangId]) {
            return this.questionsTranslationsGroup[contentLangId].some((translation) => translation.Value.trim());
        }
        return false;
    }

    isAnonymousAnswers(formItem: { IsAnonymousAnswers: boolean }) {
        return formItem.IsAnonymousAnswers;
    }

    private getNewTextQuestionInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.Text,
            IsMandatory: false,
            IsDisabled: false,
            uid: this.getUniqueId(),
        };
    }

    private getNewNumericQuestionInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.Numeric,
            IsMandatory: false,
            IsDisabled: false,
            uid: this.getUniqueId(),
        };
    }

    private getNewSingleChoiceQuestionInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.SingleChoice,
            PresetOptions: [],
            IsMandatory: false,
            IsDisabled: false,
            uid: this.getUniqueId(),
        };
    }

    private getNewMultipleChoiceQuestionInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.MultipleChoice,
            PresetOptions: [],
            IsMandatory: false,
            IsDisabled: false,
            uid: this.getUniqueId(),
        };
    }

    private getNewHeadingInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.Heading,
            uid: this.getUniqueId(),
        };
    }

    private getNewTextMessageInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.TextMessage,
            uid: this.getUniqueId(),
        };
    }

    private getNewMediaBankInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.MediaBank,
            uid: this.getUniqueId(),
        };
    }

    private getNewFileAttachmentInstance(): IFormQuestion {
        return {
            Names: [],
            MediaBankFileReferences: [],
            Type: FormQuestionType.Attachment,
            uid: this.getUniqueId(),
            UploadButton: {
                Names: [],
            },
        };
    }

    getNewConditionActionInstance(): IFormConditionAction {
        return {
            Action: undefined,
            targetQuestionUid: undefined,
        };
    }

    isFormNameRequired(formNames: IContentLanguageLabelModel[]): boolean {
        return !formNames.some((name) => name.Value);
    }

    isQuestionNameRequired(questionName: IContentLanguageLabelModel, isFormTranslated: boolean): boolean {
        return (
            !isFormTranslated ||
            (this.isAnyQuestionTranslated(questionName.ContentLanguageId) && questionName.Value.trim().length === 0)
        );
    }

    isMediaBankFileReferenceForSelectedLanguage(mbRef: IMediaBankFileReference, contentLanguageId: number) {
        return !isDefined(mbRef.ContentLanguageId) || mbRef.ContentLanguageId === contentLanguageId;
    }
}
